import { useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { nativeToDisplayCoordinates } from '@utils/video/helpers';

const Canvas = styled.canvas`
	position: absolute;
	pointer-events: none;
	z-index: 10;
`;

/**
 * Pure rendering component that draws a virtual line over a video element.
 * Accepts the video DOM element directly (not a ref) so React can
 * trigger re-renders when the element becomes available.
 */
const VirtualLineCanvas = ({ videoElement, startPoint, endPoint, lineColor = '#198754' }) => {
	const canvasRef = useRef(null);

	const syncSize = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas || !videoElement) return false;
		if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) return false;

		const containerRect = videoElement.getBoundingClientRect();
		if (containerRect.width === 0 || containerRect.height === 0) return false;

		const videoAspect = videoElement.videoWidth / videoElement.videoHeight;
		const containerAspect = containerRect.width / containerRect.height;

		let renderWidth, renderHeight, offsetX, offsetY;

		if (videoAspect > containerAspect) {
			renderWidth = containerRect.width;
			renderHeight = containerRect.width / videoAspect;
			offsetX = 0;
			offsetY = (containerRect.height - renderHeight) / 2;
		} else {
			renderHeight = containerRect.height;
			renderWidth = containerRect.height * videoAspect;
			offsetX = (containerRect.width - renderWidth) / 2;
			offsetY = 0;
		}

		canvas.width = renderWidth;
		canvas.height = renderHeight;
		canvas.style.left = `${offsetX}px`;
		canvas.style.top = `${offsetY}px`;
		canvas.style.width = `${renderWidth}px`;
		canvas.style.height = `${renderHeight}px`;

		return true;
	}, [videoElement]);

	const drawLine = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas || !videoElement) return;

		try {
			const ctx = canvas.getContext('2d');
			if (!ctx) return;

			ctx.clearRect(0, 0, canvas.width, canvas.height);

			if (!startPoint || !endPoint) return;

			const [sx, sy] = nativeToDisplayCoordinates(
				startPoint[0],
				startPoint[1],
				videoElement,
				canvas
			);
			const [ex, ey] = nativeToDisplayCoordinates(
				endPoint[0],
				endPoint[1],
				videoElement,
				canvas
			);

			ctx.strokeStyle = lineColor;
			ctx.lineWidth = 3;
			ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
			ctx.shadowBlur = 4;
			ctx.beginPath();
			ctx.moveTo(sx, sy);
			ctx.lineTo(ex, ey);
			ctx.stroke();
		} catch (err) {
			console.error('VirtualLineCanvas draw error:', err);
		}
	}, [startPoint, endPoint, lineColor, videoElement]);

	// Sync canvas size and draw line
	useEffect(() => {
		if (!videoElement) return;

		const syncAndDraw = () => {
			if (syncSize()) drawLine();
		};

		syncAndDraw();

		// ResizeObserver catches all layout changes (sidebar toggle, flex reflow, etc.)
		const resizeObserver = new ResizeObserver(syncAndDraw);
		resizeObserver.observe(videoElement);

		videoElement.addEventListener('loadedmetadata', syncAndDraw);

		return () => {
			resizeObserver.disconnect();
			videoElement.removeEventListener('loadedmetadata', syncAndDraw);
		};
	}, [videoElement, syncSize, drawLine]);

	// Redraw on timeupdate (keeps line visible during playback)
	useEffect(() => {
		if (!videoElement) return;

		const onTimeUpdate = () => drawLine();
		videoElement.addEventListener('timeupdate', onTimeUpdate);
		return () => videoElement.removeEventListener('timeupdate', onTimeUpdate);
	}, [videoElement, drawLine]);

	return <Canvas ref={canvasRef} />;
};

export default VirtualLineCanvas;
